import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import PasswordInput from "@/components/PasswordInput";
import { Lightning, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login, logout, user, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);   // v3.2 · true when user hit "Switch account"
  const nav = useNavigate();

  // v3.2 · Previously we hard-redirected any signed-in visitor away
  // from /login. That trapped users on this device — you couldn't
  // reach the form to sign in as a second account. Now we render the
  // form regardless, and show a "signed in as X" banner with a
  // one-click Continue-to-dashboard link OR Switch-account button
  // that clears the current session first.
  const isSignedIn = !!user && user !== false;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    // If a session already exists, sign it out first so the new cookie
    // cleanly replaces the previous one and any in-memory state resets.
    if (isSignedIn && !switching) {
      await logout();
    }
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/dashboard");
  };

  const switchAccount = async () => {
    setSwitching(true);
    await logout();
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10] flex flex-col">
      <Topbar />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex relative bp-grid border-r border-white/10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/10 via-transparent to-transparent" />
          <div className="relative m-auto p-12 max-w-md">
            <Lightning size={36} weight="fill" className="text-[#00E5FF]" />
            <h2 className="mt-6 font-display text-4xl font-black tracking-tighter">Welcome back, engineer.</h2>
            <p className="mt-4 text-gray-400">Sign in to continue designing compliant panels with EEngDeS Studio.</p>
            <div className="mt-10 font-mono text-xs text-gray-500 space-y-1">
              <div>$ eengdes auth login</div>
              <div className="text-[#00E5FF]">→ session resumed.</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-8 md:p-12">
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
            <div className="label-tag">// Sign in</div>
            <h1 className="font-display text-3xl font-black mt-2">Access your studio</h1>
            <p className="text-sm text-gray-400 mt-2">Use any email — personal or work.</p>

            {isSignedIn && !switching && (
              <div className="mt-6 p-3 border border-[#FFCC00]/40 bg-[#FFCC00]/5 text-xs font-mono text-[#FFCC00]"
                   data-testid="login-already-signed-in-banner">
                Signed in as <span className="text-white">{user.email}</span>.<br />
                <button type="button" onClick={() => nav("/dashboard")}
                        className="underline hover:text-white mr-3"
                        data-testid="login-continue-btn">Continue to dashboard</button>
                or
                <button type="button" onClick={switchAccount}
                        className="underline hover:text-white ml-3"
                        data-testid="login-switch-account-btn">Sign in as different user</button>
                <div className="mt-2 text-[10px] text-gray-500">
                  Need both accounts open at the same time?{" "}
                  <button type="button" onClick={() => window.open("/login?tab_session=1", "_blank")}
                          className="underline text-[#00E5FF] hover:text-white"
                          data-testid="login-open-tab-session-btn">
                    Open in a new tab
                  </button>
                  {" "}— that tab keeps its own session, this one stays as {user.email}.
                </div>
              </div>
            )}
            {switching && (
              <div className="mt-6 p-3 border border-[#00E5FF]/40 bg-[#00E5FF]/5 text-xs font-mono text-[#00E5FF]"
                   data-testid="login-switching-banner">
                Previous session ended. Enter the new credentials below.
              </div>
            )}

            {error && (
              <div className="mt-6 p-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-mono" data-testid="login-error">
                {error}
              </div>
            )}

            <label className="block mt-6">
              <span className="label-tag">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-black/40 border border-white/10 px-3 py-3 text-white font-mono text-sm focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
                data-testid="login-email-input"
                placeholder="you@example.com"
              />
            </label>

            <label className="block mt-4">
              <span className="label-tag">Password</span>
              <div className="mt-2">
                <PasswordInput
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  placeholder="••••••••"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : (<>Sign in <ArrowRight size={18} weight="bold" /></>)}
            </button>

            <div className="mt-3 text-right">
              <Link to="/forgot-password" className="text-xs font-mono text-gray-400 hover:text-[#00E5FF]" data-testid="link-forgot-password">
                Forgot password?
              </Link>
            </div>

            <p className="mt-6 text-sm text-gray-400">
              New here?{" "}
              <Link to="/register" className="text-[#00E5FF] hover:underline" data-testid="link-register">
                Create an account
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
